'use client'

import { useEffect, useState, useCallback } from 'react'
import { useAuth } from '@/hooks/use-auth'
import { usePlanTenantCountsQuery } from '@/hooks/queries/billing-plan-tenants'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { Loader2, Plus, Pencil, Trash2, AlertCircle, Clock, ArchiveRestore, Users } from 'lucide-react'
import { PlanEditor } from './plan-editor/plan-editor'
import type { PlanPayload } from './plan-editor/types'
import { PlanConflictError } from '@/lib/billing/plans/plan-write'
import { PlanCardBody, PlanCreditsLine } from './plan-card-body'
import { PlanChannelFilter } from './plan-channel-filter'
import { formatCentsAsUsd } from '@/lib/billing/utils/format'
import { BILLING_CHANNELS, PLAN_STATUSES, type BillingChannel } from '@/lib/billing/constants'
import type { BillingPlan } from '@/lib/billing/types'
import type { PlanTenantCount } from '@/lib/billing/plans/plan-tenants'

const NO_TENANTS: PlanTenantCount = { tenants: 0, archived: 0 }

export function PlansTab({ canManage, onEditingChange }: { canManage: boolean; onEditingChange: (editing: boolean) => void }) {
  const { fetchWithAuth } = useAuth()
  const { data: tenantCounts } = usePlanTenantCountsQuery()
  const [plans, setPlans] = useState<BillingPlan[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [editorOpen, setEditorOpen] = useState(false)
  const [editingPlan, setEditingPlan] = useState<BillingPlan | null>(null)

  const [filterChannel, setFilterChannel] = useState<BillingChannel>(BILLING_CHANNELS.STRIPE)
  const [filterStatus, setFilterStatus] = useState<string>(PLAN_STATUSES.PUBLISHED)

  const fetchPlans = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const res = await fetchWithAuth('/api/admin/billing/plans')
      if (!res.ok) throw new Error('Failed to fetch plans')
      const data = await res.json()
      setPlans(data.plans ?? [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch plans')
    } finally {
      setLoading(false)
    }
  }, [fetchWithAuth])

  useEffect(() => { fetchPlans() }, [fetchPlans])

  const handleCreate = () => {
    setEditingPlan(null)
    setEditorOpen(true)
    onEditingChange(true)
  }

  const handleEdit = (plan: BillingPlan) => {
    setEditingPlan(plan)
    setEditorOpen(true)
    onEditingChange(true)
  }

  const handleDelete = async (plan: BillingPlan) => {
    if (!confirm(`Archive plan "${plan.display_name}"? It will be hidden from assignment and pricing.`)) return
    try {
      const res = await fetchWithAuth(`/api/admin/billing/plans/${plan.id}`, { method: 'DELETE' })
      if (!res.ok) {
        const data = await res.json()
        alert(data.error || 'Failed to archive plan')
        return
      }
      fetchPlans()
    } catch {
      alert('Failed to archive plan')
    }
  }

  const handleRestore = async (plan: BillingPlan) => {
    if (!confirm(`Restore "${plan.display_name}" to draft? You can publish it again from the edit form.`)) return
    try {
      const res = await fetchWithAuth(`/api/admin/billing/plans/${plan.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'draft' }),
      })
      if (!res.ok) {
        const data = await res.json()
        alert(data.error || 'Failed to restore plan')
        return
      }
      fetchPlans()
    } catch {
      alert('Failed to restore plan')
    }
  }

  const handleSave = async (data: PlanPayload, expectedUpdatedAt: string | null) => {
    const url = editingPlan
      ? `/api/admin/billing/plans/${editingPlan.id}`
      : '/api/admin/billing/plans'
    const method = editingPlan ? 'PATCH' : 'POST'

    const res = await fetchWithAuth(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(editingPlan ? { ...data, expected_updated_at: expectedUpdatedAt } : data),
    })

    if (!res.ok) {
      const err = await res.json()
      // Other 409s (system Free plan, live subscriptions) carry no `conflict`.
      if (err.conflict) throw new PlanConflictError(err.conflict.changedAt, err.conflict.changedBy, err.plan)
      throw new Error(err.error || 'Failed to save plan')
    }

    setEditorOpen(false)
    onEditingChange(false)
    // Show the saved plan: a new draft or Shopify plan would otherwise vanish behind the filters.
    setFilterChannel(data.billing_channel as BillingChannel)
    if (!matchesStatus(data.status, filterStatus)) setFilterStatus(data.status)
    fetchPlans()
  }

  const statusPlans = plans.filter(plan => matchesStatus(plan.status, filterStatus))
  const channelCounts = {
    [BILLING_CHANNELS.STRIPE]: statusPlans.filter(plan => plan.billing_channel === BILLING_CHANNELS.STRIPE).length,
    [BILLING_CHANNELS.SHOPIFY]: statusPlans.filter(plan => plan.billing_channel === BILLING_CHANNELS.SHOPIFY).length,
  }
  const filteredPlans = statusPlans.filter(plan => plan.billing_channel === filterChannel)

  if (editorOpen && canManage) {
    // Keyed by version so a conflict reload remounts on the latest plan.
    return <PlanEditor key={`${editingPlan?.id ?? 'new'}:${editingPlan?.updated_at ?? ''}`} plan={editingPlan} onSave={handleSave} onClose={() => {
      setEditorOpen(false)
      onEditingChange(false)
      void fetchPlans()
    }} onReload={latest => {
      setEditingPlan(latest)
      void fetchPlans()
    }} />
  }

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (error) {
    return <div className="text-center text-destructive py-12">{error}</div>
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold">Billing Plans</h2>
        <div className="flex flex-wrap gap-2">
          <PlanChannelFilter value={filterChannel} counts={channelCounts} onChange={setFilterChannel} />
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger aria-label="Status" className="w-[130px] h-8 text-xs">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active (no archived)</SelectItem>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="published">Published</SelectItem>
              <SelectItem value="internal">Internal</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
            </SelectContent>
          </Select>
          {canManage && (
            <Button onClick={handleCreate} size="sm">
              <Plus className="h-4 w-4 mr-1" /> <span>Add Plan</span>
            </Button>
          )}
        </div>
      </div>

      {filteredPlans.length === 0 ? (
        <div className="text-center text-muted-foreground py-12">
          {plans.length === 0 ? 'No plans configured' : 'No plans match filters'}
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {filteredPlans.map((plan) => (
            <PlanAdminCard key={plan.id} plan={plan} tenants={tenantCounts ? tenantCounts[plan.id] ?? NO_TENANTS : undefined} canManage={canManage} onEdit={handleEdit} onDelete={handleDelete} onRestore={handleRestore} />
          ))}
        </div>
      )}
    </div>
  )
}

function matchesStatus(status: string, filter: string): boolean {
  switch (filter) {
    case 'active': return status !== PLAN_STATUSES.ARCHIVED
    case 'all': return true
    default: return status === filter
  }
}

function PlanAdminCard({ plan, tenants, canManage, onEdit, onDelete, onRestore }: {
  plan: BillingPlan
  /** Undefined until the counts load. */
  tenants?: PlanTenantCount
  canManage: boolean
  onEdit: (plan: BillingPlan) => void
  onDelete: (plan: BillingPlan) => void
  onRestore: (plan: BillingPlan) => void
}) {
  const isFree = plan.price_monthly_cents === 0
  const isArchived = plan.status === 'archived'

  return (
    <Card className={`flex flex-col ${plan.status === 'draft' || isArchived ? 'opacity-60' : ''}`}>
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="min-w-0 break-words text-base">{plan.display_name}</CardTitle>
          <div className="flex flex-wrap items-center gap-1">
            <SyncIndicator plan={plan} />
            {statusBadge(plan.status)}
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="min-w-0 break-all text-xs text-muted-foreground">{plan.name}</p>
          {tenants === undefined ? null : <TenantCount count={tenants} />}
        </div>
        <p className="text-2xl font-bold mt-1">
          {isFree ? 'Free' : `${formatCentsAsUsd(plan.price_monthly_cents)}/mo`}
        </p>
        <PlanCreditsLine plan={plan} />
      </CardHeader>

      <CardContent className="flex flex-col flex-1 gap-3 pt-0">
        <div className="flex gap-3 text-xs text-muted-foreground">
          <span>Text x{plan.text_cost_multiplier}</span>
          <span>Voice x{plan.voice_cost_multiplier}</span>
        </div>

        <PlanCardBody plan={plan} maxBenefits={4} />

        {canManage && (
          <div className="flex gap-2 mt-2 pt-2 border-t">
            <Button variant="outline" size="sm" className="flex-1" onClick={() => onEdit(plan)}>
              <Pencil className="h-3.5 w-3.5 mr-1" /> <span>Edit</span>
            </Button>
            {isArchived ? (
              <Button variant="ghost" size="sm" title="Restore to draft" onClick={() => onRestore(plan)}>
                <ArchiveRestore className="h-3.5 w-3.5" />
              </Button>
            ) : (
              <Button variant="ghost" size="sm" title="Archive" onClick={() => onDelete(plan)}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function statusBadge(status: string) {
  switch (status) {
    case 'published': return <Badge variant="default">Published</Badge>
    case 'internal': return <Badge variant="secondary">Internal</Badge>
    case 'archived': return <Badge variant="outline" className="text-muted-foreground">Archived</Badge>
    default: return <Badge variant="outline">Draft</Badge>
  }
}

function SyncIndicator({ plan }: { plan: BillingPlan }) {
  if (plan.billing_channel !== 'stripe' || plan.price_monthly_cents === 0 || plan.status === 'archived') return null
  switch (plan.stripe_sync_status) {
    case 'failed':
      return <span className="inline-flex items-center text-destructive" title={plan.stripe_sync_error ?? 'Stripe sync failed — open plan to retry'}><AlertCircle className="h-4 w-4" /></span>
    case 'pending':
      return <span className="inline-flex items-center text-amber-600" title="Stripe sync pending"><Clock className="h-4 w-4" /></span>
    case null:
      return <span className="inline-flex items-center text-amber-600" title="Not synced to Stripe yet — open plan to sync"><AlertCircle className="h-4 w-4" /></span>
    case 'synced':
      return null
  }
}

function TenantCount({ count }: { count: PlanTenantCount }) {
  return (
    <span className="inline-flex shrink-0 flex-wrap items-center gap-1.5 text-xs">
      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 ${count.tenants > 0 ? 'bg-violet-50 font-medium text-violet-900 dark:bg-violet-950/40 dark:text-violet-100' : 'bg-muted text-muted-foreground'}`}>
        <Users aria-hidden="true" className="h-3 w-3" />
        <span>{tenantLabel(count.tenants)}</span>
      </span>
      {count.archived > 0
        ? <span className="text-muted-foreground" title="Archived tenants keep their subscription until purge, so the plan cannot be archived yet.">{`+${count.archived} archived`}</span>
        : null}
    </span>
  )
}

function tenantLabel(count: number): string {
  switch (count) {
    case 0: return 'No tenants'
    case 1: return '1 tenant'
    default: return `${count} tenants`
  }
}
